<template>
  <Swiper
    :class="[
      'organism organism-slider',
      'container-' + organism.containerWidth,
      organism.removeMargin,
      sliderId,
      isSlideHidden ? 'hide-duplicate-slide' : '',
    ]"
    :navigation="true"
    :loop="true"
    :modules="modules"
    :pagination="true"
    :autoplay="autoplaySlider()"
    @init="onSwiper"
  >
    <SwiperSlide
      v-for="(organism, index) in organism.item"
      :key="'slider-organism' + index"
      class="select-none"
    >
      <component
        :id="organism.id ? organism.id : 'organism-' + (index + 1)"
        :class="[organism.cssClasses, organism.type, 'organism-' + (index + 1)]"
        :is="
          organism.type === 'brand-window' &&
          organism.brandWindowLayout === 'box-overlay'
            ? OrganismsBWBoxOverlay
            : organism.type === 'brand-window' &&
              organism.brandWindowLayout === 'split-slant'
            ? OrganismsBWSlant
            : organism.type === 'brand-window' &&
              (organism.brandWindowLayout === 'text-overlay-left' ||
                organism.brandWindowLayout === 'text-overlay-center')
            ? OrganismsBWTextOverlay
            : organism.type === 'image'
            ? OrganismsImage
            : organism.type === 'simple-centered'
            ? OrganismsSimpleCentered
            : organism.type === 'split-content'
            ? OrganismsSplitContent
            : organism.type === 'testimonial-cta'
            ? OrganismsTestimonialCTA
            : organism.type === 'virtual-tour'
            ? OrganismsVirtualTour
            : organism.type === 'wysiwyg-block'
            ? OrganismsWysiwygBlock
            : 'div'
        "
        :organism="organism"
        :slide-index="index"
      />
    </SwiperSlide>
  </Swiper>
</template>

<style>
.organism-slider {
  &.hide-duplicate-slide {
    .swiper-slide-duplicate {
      @apply first:hidden;
    }
  }

  &.swiper {
    --swiper-theme-color: #559cbe;
    --swiper-navigation-size: 1.5rem;
  }

  .swiper-button-prev,
  .swiper-button-next {
    @apply bg-white w-10 h-10 rounded-full shadow-lg;
  }

  /* 9px rather than the old 4px on mobile: when this slider is full-width it
     uses the .container-full-width breakout, which is 100vw and so overhangs
     the visible area by ~7.5px a side. At 4px the arrows ended up ~3.5px past
     the edge and got clipped. Desktop's 10px already cleared it. */
  .swiper-button-prev {
    @apply left-[9px] md:left-[10px];
  }

  .swiper-button-next {
    @apply right-[9px] md:right-[10px];
  }

  .swiper-button-prev:hover,
  .swiper-button-next:hover {
    @apply bg-gray-200;
  }
}
</style>

<script setup>
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, Pagination, Autoplay } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/autoplay";

const modules = [Navigation, Pagination, Autoplay];

const OrganismsBWBoxOverlay = resolveComponent("OrganismsBWBoxOverlay");
const OrganismsBWSlant = resolveComponent("OrganismsBWSlant");
const OrganismsBWTextOverlay = resolveComponent("OrganismsBWTextOverlay");
const OrganismsImage = resolveComponent("OrganismsImage");
const OrganismsSimpleCentered = resolveComponent("OrganismsSimpleCentered");
const OrganismsSplitContent = resolveComponent("OrganismsSplitContent");
const OrganismsTestimonialCTA = resolveComponent("OrganismsTestimonialCTA");
const OrganismsVirtualTour = resolveComponent("OrganismsVirtualTour");
const OrganismsWysiwygBlock = resolveComponent("OrganismsWysiwygBlock");

const props = defineProps({
  organism: Object,
});

const sliderId = "slider-id-" + generateRandomString(8);
const isSlideHidden = ref(true);

const onSwiper = (swiper) => {
  isSlideHidden.value = false;
};

const adjustSlideHeights = () => {
  nextTick(() => {
    const slides = document.querySelectorAll(
      "." + sliderId + " .swiper-slide > .organism",
    );
    let maxHeight = 0;

    // Reset the height to auto to recalculate based on natural height
    slides.forEach((slide) => {
      slide.style.height = "auto";
      const slideHeight = slide.offsetHeight;
      if (slideHeight > maxHeight) {
        maxHeight = slideHeight;
      }
    });

    // Set all slides to the tallest height
    slides.forEach((slide) => {
      slide.style.height = maxHeight + "px";
    });
  });
};

onMounted(() => {
  if (props.organism.containerWidth === "full-width") {
    addFullWidthSupport();
  }

  if (props.organism.equalizeSlideHeights) {
    adjustSlideHeights();
    window.addEventListener("resize", adjustSlideHeights);
  }
});

onUnmounted(() => {
  if (props.organism.equalizeSlideHeights) {
    window.removeEventListener("resize", adjustSlideHeights);
  }
});

const autoplaySlider = () => {
  if (props.organism.autoplaySlider && props.organism.autoplayDelay) {
    return { delay: props.organism.autoplayDelay };
  }

  return false;
};
</script>
